import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import showToast from '@/utils/toast';
import { styled } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { uploadWithPresignedUrl } from '@/utils/file';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { useUploadPresignedUrl } from '@/hooks/requests/usePresignedUrls';
import { PresignedUrlRequestTypes } from '@/types/common';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type FileUploadProps = {
  label: string;
  name: string;
  documentDisplayName: string;
  uuid: string | null;
  rules?: InputRules;
  acceptedFileTypes?: string;
  testId?: string;
  presignedUrlFileType: PresignedUrlRequestTypes;
  maxFileSizeMB?: number;
  onUploadSuccess?: () => void;
  onRemoveFile?: () => void;
};

export default function SVFilePicker({
  label,
  name,
  documentDisplayName,
  uuid,
  rules,
  acceptedFileTypes = '.png,.jpg,.jpeg',
  testId = 'fileUploadInput',
  presignedUrlFileType,
  maxFileSizeMB = 10,
  onUploadSuccess,
  onRemoveFile,
}: FileUploadProps) {
  const { uploadPresignedUrl } = useUploadPresignedUrl();

  const methods = useFormContext();
  const [isUploading, setIsUploading] = useState(false);
  const builtRulesObject = buildInputRulesObject(label, rules);

  const displayName = methods.watch(documentDisplayName);

  const removeFile = () => {
    if (onRemoveFile) {
      onRemoveFile();
    }
    methods.setValue(name, '');
    methods.setValue(documentDisplayName, '');
  };

  if (displayName) {
    return (
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ color: 'primary' }} fontWeight={'bold'}>
          {displayName}
        </Typography>
        <IconButton onClick={removeFile} aria-label={`remove ${label} file`}>
          <Delete />
        </IconButton>
      </Box>
    );
  }

  const validateFileSize = (file: File): boolean => {
    if (!maxFileSizeMB) {
      return true;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSizeMB) {
      showToast({
        type: 'error',
        data: {
          title: 'File Too Large',
          content: `${file.name} exceeds the maximum file size of ${maxFileSizeMB}MB. File size: ${fileSizeMB.toFixed(2)}MB`,
        },
      });
      return false;
    }
    return true;
  };

  const handleUploadFileToS3 = async (file: File) => {
    const validSize = validateFileSize(file);
    if (!validSize) {
      return;
    }

    setIsUploading(true);

    try {
      const preSignedUrlResponse = await uploadPresignedUrl({
        file: file.name,
        type: presignedUrlFileType,
        uuid,
      });

      const uploadImageFileResponse = await uploadWithPresignedUrl(
        preSignedUrlResponse,
        file,
      );

      if (onUploadSuccess) {
        onUploadSuccess();
      }
      methods.setValue(name, uploadImageFileResponse);
      methods.setValue(documentDisplayName, file.name);
      setIsUploading(false);
    } catch {
      setIsUploading(false);
      showToast({
        type: 'error',
        data: { content: `Upload file ${file.name} failed` },
      });
    }
  };

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={builtRulesObject}
      render={({ field: { value, onBlur, ref }, fieldState: { error } }) => {
        if (isUploading) {
          return <CircularProgress />;
        }

        return (
          <>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUpload />}>
              {label}
              <VisuallyHiddenInput
                data-testid={testId}
                value={value?.fileName}
                onChange={event => {
                  if (event.target.files) {
                    handleUploadFileToS3(event.target.files[0]);
                  }
                }}
                onBlur={onBlur}
                ref={ref}
                type="file"
                id="picture"
                accept={acceptedFileTypes}
              />
            </Button>
            {error && (
              <Typography sx={{ color: 'error.main' }}>
                {error?.message}
              </Typography>
            )}
            <Typography variant="caption" color="grey">
              Accepted file types: (
              {acceptedFileTypes.toUpperCase().replace(',', ', ')})
              {maxFileSizeMB
                ? `. The files must be less
              than ${maxFileSizeMB}MB`
                : ''}
            </Typography>
          </>
        );
      }}
    />
  );
}
